import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeSelector from '../components/ThemeSelector';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

interface OnboardingStep {
    id: string;
    title: string;
    description: string;
}

const steps: OnboardingStep[] = [
    {
        id: 'welcome',
        title: 'Welcome to Stellar! 🌟',
        description: 'Let\'s get your workspace set up in just a few steps.',
    },
    {
        id: 'theme',
        title: 'Choose Your Theme',
        description: 'Pick a look that matches your company\'s style.',
    },
    {
        id: 'modules',
        title: 'Enable Features',
        description: 'Select the modules you want to use.',
    },
    {
        id: 'complete',
        title: 'You\'re All Set! 🎉',
        description: 'Your workspace is ready. Let\'s explore!',
    },
];

export default function OnboardingWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { tenant } = useTenant();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedModules, setSelectedModules] = useState<string[]>(['bingo', 'leave_tracker']);

    const step = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;

    const handleNext = () => {
        if (isLastStep) {
            // Save preferences and redirect to dashboard
            navigate('/');
        } else {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        navigate('/');
    };

    const toggleModule = (module: string) => {
        setSelectedModules(prev =>
            prev.includes(module)
                ? prev.filter(m => m !== module)
                : [...prev, module]
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl">
                {/* Progress */}
                <div className="flex justify-between mb-8">
                    {steps.map((s, i) => (
                        <div key={s.id} className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${i < currentStep
                                ? 'bg-green-500 text-white'
                                : i === currentStep
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-400'
                                }`}>
                                {i < currentStep ? '✓' : i + 1}
                            </div>
                            {i < steps.length - 1 && (
                                <div className={`w-12 h-1 mx-2 ${i < currentStep ? 'bg-green-500' : 'bg-gray-700'
                                    }`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Card */}
                <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-700 p-8">
                    <h2 className="text-2xl font-bold text-white mb-2">{step.title}</h2>
                    <p className="text-gray-400 mb-8">{step.description}</p>

                    {/* Step Content */}
                    {step.id === 'welcome' && (
                        <div className="text-center py-8">
                            <div className="text-6xl mb-4">⭐</div>
                            <p className="text-gray-300 text-lg">
                                Hi {user?.name || 'there'}! Welcome to <span className="text-purple-400 font-bold">{tenant?.name || 'Stellar'}</span>.
                            </p>
                            <p className="text-gray-400 mt-2">
                                We'll help you personalize your experience in the next few steps.
                            </p>
                        </div>
                    )}

                    {step.id === 'theme' && (
                        <ThemeSelector />
                    )}

                    {step.id === 'modules' && (
                        <div className="space-y-4">
                            {[
                                { id: 'bingo', name: 'Bingo Games', icon: '🎯', desc: 'Team building and engagement bingo' },
                                { id: 'leave_tracker', name: 'Leave Tracker', icon: '📅', desc: 'Manage team leaves and approvals' },
                            ].map(module => (
                                <button
                                    key={module.id}
                                    onClick={() => toggleModule(module.id)}
                                    className={`w-full p-4 rounded-lg border-2 flex items-center gap-4 transition ${selectedModules.includes(module.id)
                                        ? 'border-purple-500 bg-purple-500/20'
                                        : 'border-gray-600 hover:border-gray-500'
                                        }`}
                                >
                                    <span className="text-3xl">{module.icon}</span>
                                    <div className="text-left">
                                        <p className="font-medium text-white">{module.name}</p>
                                        <p className="text-sm text-gray-400">{module.desc}</p>
                                    </div>
                                    {selectedModules.includes(module.id) && (
                                        <span className="ml-auto text-green-400 text-xl">✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {step.id === 'complete' && (
                        <div className="text-center py-8">
                            <div className="text-6xl mb-4">🚀</div>
                            <p className="text-gray-300 text-lg">
                                Your workspace is ready to go!
                            </p>
                            <div className="mt-6 p-4 bg-gray-700/50 rounded-lg text-left">
                                <p className="text-sm text-gray-400 mb-2">Quick tips:</p>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• Explore the Dashboard to see active games</li>
                                    <li>• Invite your team from Admin → Invitations</li>
                                    <li>• Customize branding in Admin → Branding</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
                        <div>
                            {currentStep > 0 && (
                                <button
                                    onClick={handleBack}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition"
                                >
                                    ← Back
                                </button>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleSkip}
                                className="px-4 py-2 text-gray-400 hover:text-white transition"
                            >
                                Skip for now
                            </button>
                            <button
                                onClick={handleNext}
                                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
                            >
                                {isLastStep ? 'Get Started' : 'Continue →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
